import { useRef } from 'preact/hooks';
import { usePickerSheet } from '../hooks/usePickerSheet';
import styles from './TemplatePickerSheet.module.css';

export interface ArcOption {
  key: string; // matches backend EMOTIONAL_ARC_SHAPES key (e.g. RAGS_TO_RICHES)
  label: string;
  description: string;
}

// Mirrors EMOTIONAL_ARC_SHAPES in storythreads-api/constants/arcs.ts. Display
// names match the OurTake-branded names defined alongside each shape.
export const ARC_SHAPE_OPTIONS: ArcOption[] = [
  { key: 'RAGS_TO_RICHES', label: 'Glow Up', description: 'Rise from struggle to triumph. Hopeful, empowered, building.' },
  { key: 'RICHES_TO_RAGS', label: 'Plot Twist', description: 'Fall from a high. Bittersweet, examined through loss.' },
  { key: 'ICARUS', label: 'Too Close to the Sun', description: 'Rise to a peak, then a hard fall. Hubris and hard-won wisdom.' },
  { key: 'OEDIPUS', label: 'Rollercoaster', description: 'Fall, rise, fall. Reversals that refuse to settle.' },
  { key: 'CINDERELLA', label: 'Comeback', description: 'Rise, fall, rise. Setback tests, then vindication.' },
  { key: 'MAN_IN_HOLE', label: 'Bounce Back', description: 'Fall into difficulty, then recover. The most universal arc.' },
];

interface ArcPickerSheetProps {
  open: boolean;
  onSelect: (arcKey: string) => void;
  onDismiss: () => void;
  mode?: 'proactive' | 'recovery';
  errorMessage?: string | null;
  options?: ArcOption[];
}

export function ArcPickerSheet({
  open,
  onSelect,
  onDismiss,
  mode = 'proactive',
  errorMessage,
  options = ARC_SHAPE_OPTIONS,
}: ArcPickerSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  usePickerSheet(open, sheetRef, onDismiss);

  if (!open) return null;

  const titleId = 'arc-picker-title';

  return (
    <div
      class={`${styles.overlay} ${styles.overlayOpen}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      <div
        ref={sheetRef}
        class={`${styles.sheet} ${styles.sheetOpen}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div class={styles.handle} />
        <div class={styles.header}>
          <h3 id={titleId} class={styles.title}>
            {mode === 'recovery' ? 'Pick a shape to keep going' : 'Pick the story’s shape'}
          </h3>
          <button
            type="button"
            class={styles.closeButton}
            onClick={onDismiss}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <p class={styles.subtitle}>
          {mode === 'recovery'
            ? 'Mood Ring stories follow a shape. Pick one to lock in the arc and post your take.'
            : 'Mood Ring stories follow a shape. Pick the one that fits — it locks in once contributions start.'}
        </p>

        {errorMessage && <div class={styles.error}>{errorMessage}</div>}

        <div class={styles.optionList}>
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              class={styles.option}
              onClick={() => onSelect(option.key)}
              aria-label={`Select ${option.label} arc`}
            >
              <span class={styles.optionTitle}>
                <span>{option.label}</span>
              </span>
              <span class={styles.optionDescription}>{option.description}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
